import { MathUtils } from './math.utils';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine.js';

export class GeometryUtils {
  static createEllipseByVertices(vertices: number[][]) {
    const ring: number[][] = [];

    const maxX = Math.max(vertices[0][0], vertices[1][0]);
    const maxY = Math.max(vertices[0][1], vertices[1][1]);
    const minX = Math.min(vertices[0][0], vertices[1][0]);
    const minY = Math.min(vertices[0][1], vertices[1][1]);

    const width = maxX - minX;
    const height = maxY - minY;

    const cX = maxX - width / 2;
    const cY = maxY - height / 2;

    for (let i = 0; i < 360; i++) {
      const t = (i * Math.PI) / 180;
      const x = cX - (width / 2) * Math.cos(t);
      const y = cY + (height / 2) * Math.sin(t);
      ring.push([x, y]);
    }
    ring.push(ring[0]);

    return ring;
  }

  static createTriangleByVertices(
    vertices: number[][],
    perfectMode = false
  ): number[][] {
    const [centerX, centerY] = vertices[0];

    const maxX = Math.max(vertices[0][0], vertices[1][0]);
    const minX = Math.min(vertices[0][0], vertices[1][0]);
    const maxY = Math.max(vertices[0][1], vertices[1][1]);
    const minY = Math.min(vertices[0][1], vertices[1][1]);

    const width = maxX - minX;
    const height = maxY - minY;

    const widthHalf = width / 2;
    const heightHalf = height / 2;

    const maxSizeHalf = Math.max(widthHalf, heightHalf);

    const distanceFromCenterY = perfectMode
      ? maxSizeHalf
      : (vertices[0][1] < vertices[1][1] ? -1 : 1) * heightHalf;

    return [
      [centerX, centerY + distanceFromCenterY],
      [
        centerX + (perfectMode ? maxSizeHalf : widthHalf),
        centerY - distanceFromCenterY,
      ],
      [
        centerX - (perfectMode ? maxSizeHalf : widthHalf),
        centerY - distanceFromCenterY,
      ],
    ];
  }

  static createArrowByVertices(
    vertices: number[][],
    width = 30,
    arrowSizeInPercent = 0.25
  ): number[][] {
    const [initialX, initialY] = vertices[0];
    const [endX, endY] = vertices[1];

    const maxX = Math.max(initialX, endX);
    const minX = Math.min(initialX, endX);
    const maxY = Math.max(initialY, endY);
    const minY = Math.min(initialY, endY);

    const lineWidth = maxX - minX;
    const lineHeight = maxY - minY;

    const maxLineSize = Math.max(lineWidth, lineHeight);

    const widthHalf = width / 2;

    const arrowSize = maxLineSize * arrowSizeInPercent;

    const angle = MathUtils.angle(initialX, initialY, endX, endY);
    const angleInverted = MathUtils.normalizeAngle(angle + 90);

    const rings: number[][] = [];

    const baseLeftPos = MathUtils.moveByAngle(
      angleInverted,
      initialX,
      initialY,
      -widthHalf
    );
    rings.push([baseLeftPos.x, baseLeftPos.y]);

    const baseRightPos = MathUtils.moveByAngle(
      angleInverted,
      initialX,
      initialY,
      widthHalf
    );
    rings.push([baseRightPos.x, baseRightPos.y]);

    const endRectangle = MathUtils.moveByAngle(angle, endX, endY, -arrowSize);

    const endRightPos = MathUtils.moveByAngle(
      angleInverted,
      endRectangle.x,
      endRectangle.y,
      widthHalf
    );
    rings.push([endRightPos.x, endRightPos.y]);

    const baseTriangleRightPos = MathUtils.moveByAngle(
      angleInverted,
      endRightPos.x,
      endRightPos.y,
      widthHalf
    );
    rings.push([baseTriangleRightPos.x, baseTriangleRightPos.y]);

    rings.push([endX, endY]);

    const endLeftPos = MathUtils.moveByAngle(
      angleInverted,
      endRectangle.x,
      endRectangle.y,
      -widthHalf
    );
    const baseTriangleLeftPos = MathUtils.moveByAngle(
      angleInverted,
      endLeftPos.x,
      endLeftPos.y,
      -widthHalf
    );
    rings.push([baseTriangleLeftPos.x, baseTriangleLeftPos.y]);
    rings.push([endLeftPos.x, endLeftPos.y]);

    return rings;
  }

  static distance(
    source: __esri.Geometry,
    target: __esri.Geometry,
    unit: __esri.LinearUnits = 'meters'
  ) {
    return geometryEngine.distance(source, target);
  }
}
